import { alpha, Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemSwitch } from "../../components/UIToolkit/Clickable/SystemSwitch";
import { NamedTimeRangeListField } from "../../components/UIToolkit/Inputs/NamedTimeRangeListField";
import { InfoIconWithTooltip } from "../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { MAX_CUSTOM_TIME_GROUPS } from "../../downloads/dailyPrintout/layout/customTimeGroups";
import type { CustomTimeGroup, CustomTimeGroupsConfig } from "../../downloads/dailyPrintout/types";
import { ALPHA, FONT_SIZE } from "../../theme/tokens";

const MINUTES_PER_DAY = 24 * 60;
const FIRST_START_MINUTE = 7 * 60;
const RANGE_DURATION_MINUTES = 8 * 60;
const NEXT_START_STEP_MINUTES = 60;

const createPrintoutTimeGroup = (existing: readonly CustomTimeGroup[]): CustomTimeGroup => {
	for (let offset = 0; offset < MINUTES_PER_DAY; offset += NEXT_START_STEP_MINUTES) {
		const startMinute = (FIRST_START_MINUTE + offset) % MINUTES_PER_DAY;
		const endMinute = (startMinute + RANGE_DURATION_MINUTES) % MINUTES_PER_DAY;
		if (!existing.some((item) => item.startMinute === startMinute && item.endMinute === endMinute)) {
			return {
				id: crypto.randomUUID(),
				startMinute,
				endMinute,
				// DEFAULT: a new group's optional name starts blank so the printout shows its time range.
				name: "",
			};
		}
	}
	throw new Error("No unique printout time range remains");
};

interface CustomTimeGroupsSettingProps {
	isDetailed: boolean;
	customTimeGroups: CustomTimeGroupsConfig;
	onCustomTimeGroupsChange: (next: CustomTimeGroupsConfig) => void;
}

/**
 * Detailed-only: named Shift Time column ranges. Rendered inside the Column Grouping section's Shift Time
 * option, so the toggle sits where its effect (Shift Time column groups) is chosen.
 */
export const CustomTimeGroupsSetting: FC<CustomTimeGroupsSettingProps> = ({ isDetailed, customTimeGroups, onCustomTimeGroupsChange }) => {
	const { t } = useTranslation();
	if (!isDetailed) return null;

	const copy = {
		startLabel: t("printout.custom.time.groups.start"),
		endLabel: t("printout.custom.time.groups.end"),
		nameLabel: t("printout.custom.time.groups.name"),
		namePlaceholder: t("printout.custom.time.groups.name.placeholder"),
		removeLabel: t("printout.custom.time.groups.remove"),
		emptyMessage: t("printout.custom.time.groups.empty"),
		addLabel: t("printout.custom.time.groups.add"),
		limitLabel: t("printout.custom.time.groups.limit", { count: MAX_CUSTOM_TIME_GROUPS }),
		sameTimeError: t("printout.custom.time.groups.same.time"),
		duplicateError: t("printout.custom.time.groups.duplicate"),
	};

	return (
		<Box
			data-testid="printout-custom-time-groups-setting"
			sx={(theme) => ({
				display: "flex",
				flexDirection: "column",
				gap: 1,
				mx: 1,
				py: 1,
				borderTop: `1px solid ${alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY)}`,
			})}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
				<Box sx={{ minWidth: 0 }}>
					<SystemSwitch
						dataTestId="printout-custom-time-groups-switch"
						checked={customTimeGroups.enabled}
						onChange={() => onCustomTimeGroupsChange({ ...customTimeGroups, enabled: !customTimeGroups.enabled })}
						label={t("printout.custom.time.groups")}
						labelFontSize={FONT_SIZE.CAPTION}
						labelWrap
					/>
				</Box>
				<InfoIconWithTooltip dataTestId="printout-custom-time-groups-info" title={t("printout.custom.time.groups.description")} />
			</Box>
			{customTimeGroups.enabled && (
				<NamedTimeRangeListField
					dataTestId="printout-custom-time-groups-editor"
					value={customTimeGroups.groups}
					onChange={(groups) => onCustomTimeGroupsChange({ ...customTimeGroups, groups })}
					maxItems={MAX_CUSTOM_TIME_GROUPS}
					copy={copy}
					createItem={createPrintoutTimeGroup}
					disabled={false}
				/>
			)}
		</Box>
	);
};
